import React, {useCallback, useEffect, useRef, useState} from 'react';
import post from '../utils/post.js';
import Search from './Search.js';
import ChartColumn from './ChartColumn.js';
import MonitorAthDonut, {athToneClass} from './MonitorAthDonut.js';
import EditableProfilePhoto from './EditableProfilePhoto.js';
import UserAvatar from './UserAvatar.js';
import UserSessionsPopup from './UserSessionsPopup.js';
import UserReservationsPopup from './UserReservationsPopup.js';
import UserLogsPopup from './UserLogsPopup.js';
import UserPaymentsPopup from './UserPaymentsPopup.js';
import UserEshopPopup from './UserEshopPopup.js';
import UserBonusPopup from './UserBonusPopup.js';
import UsersPopup from './UsersPopup.js';
import useCountdownRefresh from '../utils/useCountdownRefresh.js';

const LARGE_SCREEN_QUERY = '(min-width: 1200px)';
const REFRESH_MS = 10000;
const REFRESH_SEC = REFRESH_MS / 1000;
// Každé okno má vlastní klíč – rozbalení/sbalení se pamatuje nezávisle.
const LEFT_STORAGE_KEY = 'sysadminMonitorExpanded';
const RIGHT_STORAGE_KEY = 'sysadminMonitorRightExpanded';
// Mobil (< 1200px): jedno okno přes celou obrazovku + záložky dole.
const MOBILE_STORAGE_KEY = 'sysadminMonitorMobileExpanded';
const MOBILE_TAB_STORAGE_KEY = 'sysadminMonitorMobileTab';
// Sbalený mobilní monitor obnovuje data jen po minutě – stačí to na odznak
// s počtem věcí k řešení a stavové tečky u ikonky, a nezatěžuje to server.
const MOBILE_IDLE_REFRESH_SEC = 60;
const MOBILE_TABS = [
	{key: 'overview', label: 'Přehled'},
	{key: 'lessons', label: 'Lekce'},
	{key: 'people', label: 'Lidé'},
	{key: 'logs', label: 'Logy'},
];

const emptyLeftData = {
	kpi: {accountTotalFmt: '—', todayIncomeFmt: '—', monthIncome: 0, monthIncomeFmt: '—', monthRankFmt: '', eshopMonthFmt: '—', entriesMonthFmt: '—', athFmt: '—', athMonthFmt: '', athRefVal: 0, athRefFmt: '—', athPct: 0, athPctFmt: '—', nextLevelFmt: '—', nextLevelRemainingFmt: '', memberCntFmt: '—', permanentCntFmt: '—'},
	nuki: {hasData: false, stateLabel: '—', unlocked: false, batteryFmt: '', batteryLow: false, checkedAgoFmt: '', offline: true, error: ''},
	fio: {hasData: false, success: false, checkedAgoFmt: '', message: '', stale: false, missingColumn: false},
	backup: {hasData: false, checkedAgoFmt: '', sizeFmt: '', tone: 'danger', missingColumn: false},
	incomeMonthly: {labels: [], values: [], colors: []},
	pendingPurchases: [],
	todayPurchases: [],
	todayIncomes: [],
	lessons: [],
	expiringPermits: [],
	debtorsNoPermit: [],
	entryBans: [],
};

const emptyRightData = {
	userLogs: [],
	recentUsers: [],
};

const getInitialExpanded = (storageKey) => {
	if (typeof window === 'undefined') {
		return false;
	}

	try {
		return window.sessionStorage.getItem(storageKey) === '1';
	} catch (error) {
		return false;
	}
};

const getInitialMobileTab = () => {
	if (typeof window === 'undefined') {
		return MOBILE_TABS[0].key;
	}

	try {
		const stored = window.sessionStorage.getItem(MOBILE_TAB_STORAGE_KEY);

		return MOBILE_TABS.some((tab) => tab.key === stored) ? stored : MOBILE_TABS[0].key;
	} catch (error) {
		return MOBILE_TABS[0].key;
	}
};

const getInitialLargeScreen = () => {
	if (typeof window === 'undefined' || !window.matchMedia) {
		return false;
	}

	return window.matchMedia(LARGE_SCREEN_QUERY).matches;
};

const pad2 = (value) => String(value).padStart(2, '0');

// Částka ve formátu „1 231,00" – mezera jako oddělovač tisíců, čárka před desetinami.
const formatMoney2 = (value) => {
	const n = Number(value) || 0;
	const [intPart, decPart] = Math.abs(n).toFixed(2).split('.');
	const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

	return `${n < 0 ? '-' : ''}${grouped},${decPart}`;
};

// Počet dní do konce permice → česká hláška („dnes" / „zítra" / „za 3 dny").
const formatDaysLeft = (days) => {
	const n = Number(days);

	if (!Number.isFinite(n) || n < 0) {
		return '';
	}

	if (n === 0) {
		return 'dnes';
	}

	if (n === 1) {
		return 'zítra';
	}

	if (n >= 2 && n <= 4) {
		return `za ${n} dny`;
	}

	return `za ${n} dní`;
};

// Totéž bez předložky „za" – pro zápis za datum permice („28.8.2026 – 3 dny").
const formatDaysLeftPlain = (days) => formatDaysLeft(days).replace(/^za /, '');

// Kolik dní uplynulo od poslední návštěvy → česká hláška + tón barvy.
// dnes = zeleně, včera / 2 dny = žlutě, 3+ dní = červeně (tučně jako jméno).
const formatLastVisit = (daysAgo) => {
	if (daysAgo === null || daysAgo === undefined || daysAgo === '') {
		return null;
	}

	const n = Number(daysAgo);

	if (!Number.isFinite(n) || n < 0) {
		return null;
	}

	if (n === 0) {
		return {label: 'dnes', tone: 'today'};
	}

	if (n === 1) {
		return {label: 'včera', tone: 'recent'};
	}

	if (n === 2) {
		return {label: '2 dny', tone: 'recent'};
	}

	return {label: `${n} ${n <= 4 ? 'dny' : 'dní'}`, tone: 'stale'};
};

// Kolik času uplynulo od poslední aktivity → „10s" / „2m 42s" / „2h 38m 11s".
// Za ovál s počtem sessions v seznamu posledních aktivních uživatelů.
// withDays = true navíc vyčlení měsíce (po 30 dnech) a dny a vypustí sekundy
// („7m 5d 12h 56m") – u zákazů vstupu, kde poslední aktivita bývá dávno
// a sekundy ani samotné hodiny by nebyly přehledné.
const formatActivityAgo = (seconds, withDays = false) => {
	if (seconds === null || seconds === undefined || seconds === '') {
		return '';
	}

	const n = Number(seconds);

	if (!Number.isFinite(n) || n < 0) {
		return '';
	}

	const d = Math.floor(n / 86400);
	const h = withDays ? Math.floor((n % 86400) / 3600) : Math.floor(n / 3600);
	const m = Math.floor((n % 3600) / 60);
	const s = Math.floor(n % 60);

	if (withDays) {
		const months = Math.floor(d / 30);
		const days = d % 30;

		if (months > 0) {
			return `${months}m ${days}d ${h}h ${m}m`;
		}

		if (days > 0) {
			return `${days}d ${h}h ${m}m`;
		}

		if (h > 0) {
			return `${h}h ${m}m`;
		}

		if (m > 0) {
			return `${m}m`;
		}

		return `${s}s`;
	}

	if (h > 0) {
		return `${h}h ${m}m ${s}s`;
	}

	if (m > 0) {
		return `${m}m ${s}s`;
	}

	return `${s}s`;
};

// Kolik dní zbývá do nejbližší budoucí rezervace → česká hláška, vždy zeleně.
const formatNextVisit = (daysAhead) => {
	if (daysAhead === null || daysAhead === undefined || daysAhead === '') {
		return null;
	}

	const n = Number(daysAhead);

	if (!Number.isFinite(n) || n < 0) {
		return null;
	}

	if (n === 0) {
		return {label: 'dnes', tone: 'future'};
	}

	if (n === 1) {
		return {label: 'zítra', tone: 'future'};
	}

	return {label: `za ${n} ${n <= 4 ? 'dny' : 'dní'}`, tone: 'future'};
};

// Stav zálohy DB → tón KPI karty (backend počítá 'success'/'warning'/'danger')
// + textová hodnota (stáří zálohy, nebo „nikdy" / „chybí sloupec").
const getBackupView = (backup) => {
	if (!backup) {
		return {tone: 'home-summary-card--danger', label: 'nikdy'};
	}

	if (backup.missingColumn) {
		return {tone: 'home-summary-card--danger', label: 'chybí sloupec'};
	}

	const tone = `home-summary-card--${backup.tone || 'danger'}`;

	if (!backup.hasData) {
		return {tone, label: 'nikdy'};
	}

	return {tone, label: backup.checkedAgoFmt || '—'};
};

// Stav FIO cronu → tón KPI karty + textová hodnota.
// missingColumn = v DB ještě není sloupec config.FIO_RESULT (nutný ALTER TABLE).
const getFioView = (fio) => {
	if (!fio) {
		return {tone: '', label: '—'};
	}

	if (fio.missingColumn) {
		return {tone: '', label: 'chybí sloupec'};
	}

	if (!fio.hasData) {
		return {tone: '', label: 'bez dat'};
	}

	if (!fio.success) {
		return {tone: 'home-summary-card--danger', label: 'chyba'};
	}

	// Zpoždění hlásíme jen tónem karty – čas v závorce ukazuje, jak moc.
	if (fio.stale) {
		return {tone: 'home-summary-card--warning', label: 'OK'};
	}

	return {tone: 'home-summary-card--success', label: 'OK'};
};

// Tón karty Nuki: offline → oranžová, odemčeno → zelená, slabá baterie →
// oranžová, zamčeno → červená.
const getNukiCardTone = (nuki) => (nuki.offline
	? 'home-summary-card--warning'
	: (nuki.unlocked
		? 'home-summary-card--success'
		: (nuki.batteryLow ? 'home-summary-card--warning' : 'home-summary-card--danger')));

// Součty do hlaviček sekcí: celkový dluh (dluh + nezaplacené vstupy) červeně,
// celkové dnešní příjmy zeleně. Předplaceno (záporné TOPAY) do dluhu nepočítáme.
const sumDebtors = (debtors) => (debtors || []).reduce((sum, debtor) => {
	const topay = Number(debtor.TOPAY_TOTAL) || 0;

	return sum + (topay > 0 ? topay : 0) + (Number(debtor.NOT_PAYED_TOTAL) || 0);
}, 0);

const sumIncomes = (incomes) => (incomes || []).reduce((sum, income) => sum + (Number(income.AMOUNT) || 0), 0);

// Popisek koláče „Tržba / ATH" – Apex tooltip je vypnutý (panel má overflow: hidden),
// takže detail (kolik z kolika) nese nativní title celé karty.
const getAthShareTitle = (kpi) => ((Number(kpi.athRefVal) || 0) > 0
	? `Tržba tohoto měsíce vůči rekordu: ${kpi.monthIncomeFmt} / ${kpi.athRefFmt}${(Number(kpi.athPct) || 0) >= 100 ? ' – rekord překonán' : ''}`
	: 'Zatím není s čím porovnávat – chybí dřívější měsíc s tržbou');

// Polling s odpočtem a vlastním časem posledního obnovení. Každá instance běží
// nezávisle – vlastní timer, vlastní countdown, vlastní request (jen svá data).
// active = false → nic se nestahuje (sbalené okno, neaktivní záložka).
function useMonitorData({act, initialData, active, params = null, intervalSec = REFRESH_SEC}) {
	const [data, setData] = useState(initialData);
	const [loading, setLoading] = useState(false);
	const [errored, setErrored] = useState(false);
	const [lastUpdated, setLastUpdated] = useState('');
	const inFlightRef = useRef(false);

	// Parametry requestu (např. vyhledávání v pravém okně) drží ref – identita
	// fetchData se při psaní nemění a polling posílá vždy aktuální hodnoty.
	const paramsRef = useRef(params);
	paramsRef.current = params;

	const fetchData = useCallback(() => {
		if (inFlightRef.current) {
			return;
		}

		inFlightRef.current = true;
		setLoading(true);

		post(act, paramsRef.current || {})
			.then((res) => {
				if (res && res.success && res.data) {
					setData(res.data);
					setErrored(false);

					const now = new Date();
					setLastUpdated(`${pad2(now.getHours())}:${pad2(now.getMinutes())}:${pad2(now.getSeconds())}`);
				} else {
					setErrored(true);
				}
			})
			.catch((error) => {
				console.error(error);
				setErrored(true);
			})
			.finally(() => {
				inFlightRef.current = false;
				setLoading(false);
			});
	}, [act]);

	// Polling + odpočet: běží jen když je hook aktivní. Při skryté záložce tikání
	// pozastavíme, ale odpočet plyne dál – po návratu do aplikace se srovná
	// a zmeškaná obnova proběhne hned.
	const {secondsLeft: countdown, restart: restartCountdown} = useCountdownRefresh(intervalSec, fetchData, {
		active,
		pauseWhenHidden: true,
		refreshOnStart: true,
	});

	// Změna parametrů (vyhledávání) → okamžitá obnova + restart odpočtu, ať
	// výsledek nečeká na další tik. Timer pak sám posílá aktuální parametry.
	const paramsKey = JSON.stringify(params || {});
	const paramsKeyRef = useRef(paramsKey);

	useEffect(() => {
		if (paramsKeyRef.current === paramsKey) {
			return;
		}

		paramsKeyRef.current = paramsKey;

		if (active) {
			restartCountdown();
			fetchData();
		}
	}, [paramsKey, active, restartCountdown, fetchData]);

	const handleManualRefresh = useCallback(() => {
		restartCountdown();
		fetchData();
	}, [fetchData, restartCountdown]);

	return {data, loading, errored, lastUpdated, countdown, handleManualRefresh};
}

// Rozbalení/sbalení okna (per klíč v sessionStorage) – sdílené oběma desktopovými
// okny i mobilním monitorem.
function useExpanded(storageKey) {
	const [expanded, setExpanded] = useState(() => getInitialExpanded(storageKey));

	const persistExpanded = useCallback((next) => {
		try {
			window.sessionStorage.setItem(storageKey, next ? '1' : '0');
		} catch (error) {
			// sessionStorage nemusí být dostupné – stav zůstane jen v paměti.
		}
	}, [storageKey]);

	const toggleExpanded = useCallback(() => {
		setExpanded((prev) => {
			const next = !prev;

			persistExpanded(next);

			return next;
		});
	}, [persistExpanded]);

	// Jen sbalit (proklik z popupu na jinou stránku) – toggle by sbalené okno naopak rozbalil.
	const collapse = useCallback(() => {
		setExpanded(false);
		persistExpanded(false);
	}, [persistExpanded]);

	return [expanded, toggleExpanded, collapse];
}

// Sdílená logika obou desktopových oken: rozbalení/sbalení + polling jen když je okno rozbalené.
function useMonitorPanel({act, storageKey, initialData, active, params = null}) {
	const [expanded, toggleExpanded] = useExpanded(storageKey);
	const dataState = useMonitorData({act, initialData, active: active && expanded, params});

	return {expanded, toggleExpanded, ...dataState};
}

// Stav popupu se sekcí pro vybraného uživatele. Uživatel zůstává i po zavření –
// obsah popupu během zavírací animace nezmizí, přepíše ho až další otevření.
function useSectionPopup() {
	const [state, setState] = useState({show: false, user: {ID: 0}});

	const open = useCallback((user) => {
		setState({show: true, user});
	}, []);

	const close = useCallback(() => {
		setState((prev) => ({...prev, show: false}));
	}, []);

	return {...state, open, close};
}

// Popupy uživatele (sessions / vstupy / logy + sekce platby, eshop, bonusy, uživatelé)
// – stejné pro pravé okno i mobil. Sekce mají stejný obsah jako stránky z hlavního menu,
// jen se otevřou v popupu a aplikace se nepřepne. onNavigate = proklik z popupu na jinou
// stránku (mobil při něm sbalí monitor, jinak by stránku zakrýval).
function useUserPopups(usrInfo, onNavigate) {
	const [showSessionsPopup, setShowSessionsPopup] = useState(false);
	const [sessionsUser, setSessionsUser] = useState({ID: 0});
	const [showReservationsPopup, setShowReservationsPopup] = useState(false);
	const [reservationsUser, setReservationsUser] = useState({ID: 0});
	const [showLogsPopup, setShowLogsPopup] = useState(false);
	const [logsUser, setLogsUser] = useState({ID: 0});

	const openSessionsPopup = useCallback((user) => {
		setSessionsUser(user);
		setShowSessionsPopup(true);
	}, []);

	const closeSessionsPopup = useCallback(() => {
		setShowSessionsPopup(false);
		setSessionsUser({ID: 0});
	}, []);

	const openReservationsPopup = useCallback((user) => {
		setReservationsUser(user);
		setShowReservationsPopup(true);
	}, []);

	const closeReservationsPopup = useCallback(() => {
		setShowReservationsPopup(false);
		setReservationsUser({ID: 0});
	}, []);

	const openLogsPopup = useCallback((user) => {
		setLogsUser(user);
		setShowLogsPopup(true);
	}, []);

	const closeLogsPopup = useCallback(() => {
		setShowLogsPopup(false);
		setLogsUser({ID: 0});
	}, []);

	const paymentsPopup = useSectionPopup();
	const eshopPopup = useSectionPopup();
	const bonusPopup = useSectionPopup();
	const usersPopup = useSectionPopup();

	const popups = (
		<>
			{/* Uživatelé jsou první – logy otevřené z karty uživatele se vykreslí nad nimi. */}
			<UsersPopup show={usersPopup.show} close={usersPopup.close} search={usersPopup.user.SEARCH} name={usersPopup.user.NAME} usrInfo={usrInfo} onOpenLogs={openLogsPopup} onNavigate={onNavigate} />
			<UserPaymentsPopup show={paymentsPopup.show} close={paymentsPopup.close} user={paymentsPopup.user} usrInfo={usrInfo} onNavigate={onNavigate} />
			<UserEshopPopup show={eshopPopup.show} close={eshopPopup.close} user={eshopPopup.user} usrInfo={usrInfo} onNavigate={onNavigate} />
			<UserBonusPopup show={bonusPopup.show} close={bonusPopup.close} user={bonusPopup.user} usrInfo={usrInfo} onNavigate={onNavigate} />
			<UserSessionsPopup showSessions={showSessionsPopup} closeSessions={closeSessionsPopup} user={sessionsUser} />
			<UserReservationsPopup showReservations={showReservationsPopup} closeReservations={closeReservationsPopup} user={reservationsUser} listRows={usrInfo?.LIST_ROWS} />
			<UserLogsPopup showLogs={showLogsPopup} closeLogs={closeLogsPopup} user={logsUser} listRows={usrInfo?.LIST_ROWS} />
		</>
	);

	return {
		openSessionsPopup,
		openReservationsPopup,
		openLogsPopup,
		openPaymentsPopup: paymentsPopup.open,
		openEshopPopup: eshopPopup.open,
		openBonusPopup: bonusPopup.open,
		openUsersPopup: usersPopup.open,
		popups,
	};
}

// Sbalená ikonka (FAB). Pozici (vlevo/vpravo) řeší modifikátor v className.
function MonitorFab({className, onClick, children}) {
	return (
		<button type="button" className={`sysmon-fab ${className}`} onClick={onClick} title="Monitoring" aria-label="Otevřít monitoring">
			<svg className="sysmon-fab__icon" viewBox="0 0 24 24" aria-hidden="true">
				<path d="M3 13h3l2-6 3 12 3-9 2 3h5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
			</svg>
			{children}
		</button>
	);
}

// Hlavička okna – shodná pro obě okna (titulek, čas obnovení, refresh, sbalení).
function MonitorHeader({errored, loading, lastUpdated, countdown, onRefresh, onCollapse, title = 'Monitoring'}) {
	return (
		<header className="sysmon__head">
			<div className="sysmon__title">
				<span className="sysmon__title-dot" aria-hidden="true" />
				{title}
			</div>

			<div className="sysmon__head-right">
				<span className={`sysmon__updated${errored ? ' is-error' : ''}${loading ? ' is-loading' : ''}`}>
					{errored ? 'chyba spojení' : (lastUpdated || '…')}
				</span>

				<div className="icon_refresh_main home-refresh sysmon__refresh" onClick={onRefresh} role="button" tabIndex={0} title="Obnovit teď" aria-label="Obnovit teď">
					<div className="icon_refresh_in">{countdown}</div>
					<img className="icon_refresh" alt="" />
				</div>

				<button type="button" className="sysmon__collapse" onClick={onCollapse} aria-label="Sbalit monitoring" title="Sbalit">
					×
				</button>
			</div>
		</header>
	);
}

// KPI karty. Desktop: 6 sloupců × 2 řádky (12 karet). Mobil (showTech = false)
// vynechá FIO / Dveře / Zálohu – ty tam nese samostatný řádek stavových karet.
function KpiCards({data, showTech = true}) {
	const fioView = getFioView(data.fio);
	const backupView = getBackupView(data.backup);
	const nukiCardTone = getNukiCardTone(data.nuki);
	const athShareTitle = getAthShareTitle(data.kpi);

	return (
		<div className="sysmon__kpis">
			{/* 1. řádek (6 karet – stav a dnešek): Členové/Permice, Na účtu, Dnešní tržba, FIO cron, Dveře, Záloha DB */}
			<div className="home-summary-card sysmon__kpi">
				<img className="sysmon__kpi-icon sysmon__kpi-icon--members" alt="" />
				<div className="home-summary-card__content">
					<span className="home-summary-card__eyebrow" title="Členové / Permice">Člen. / Perm.</span>
					<span className="home-summary-card__value">{data.kpi.memberCntFmt} <span className="sysmon__kpi-sep">/</span> {data.kpi.permanentCntFmt}</span>
				</div>
			</div>

			<div className="home-summary-card sysmon__kpi">
				<img className="sysmon__kpi-icon sysmon__kpi-icon--account" alt="" />
				<div className="home-summary-card__content">
					<span className="home-summary-card__eyebrow">Na účtu</span>
					<span className="home-summary-card__value">{data.kpi.accountTotalFmt}</span>
				</div>
			</div>

			<div className="home-summary-card home-summary-card--success sysmon__kpi">
				<img className="sysmon__kpi-icon sysmon__kpi-icon--today" alt="" />
				<div className="home-summary-card__content">
					<span className="home-summary-card__eyebrow">Dnešní tržba</span>
					<span className="home-summary-card__value">{data.kpi.todayIncomeFmt}</span>
				</div>
			</div>

			{showTech ? (
				<>
					<div className={`home-summary-card ${fioView.tone} sysmon__kpi`}>
						<img className="sysmon__kpi-icon sysmon__kpi-icon--fio" alt="" />
						<div className="home-summary-card__content">
							<span className="home-summary-card__eyebrow" title={data.fio.message || ''}>FIO cron</span>
							<span className="home-summary-card__value">{fioView.label}{data.fio.checkedAgoFmt ? ` - ${data.fio.checkedAgoFmt}` : ''}</span>
						</div>
					</div>

					<div className={`home-summary-card ${nukiCardTone} sysmon__kpi`}>
						<img className="sysmon__kpi-icon sysmon__kpi-icon--nuki" alt="" />
						<div className="home-summary-card__content">
							<span className="home-summary-card__eyebrow">Dveře{data.nuki.batteryFmt && !data.nuki.offline ? <span className="sysmon__kpi-meta"> ({data.nuki.batteryFmt})</span> : null}</span>
							<span className="home-summary-card__value" title={data.nuki.checkedAgoFmt ? `Kontrola před ${data.nuki.checkedAgoFmt}` : ''}>{data.nuki.offline ? 'offline' : data.nuki.stateLabel}</span>
						</div>
					</div>

					<div className={`home-summary-card ${backupView.tone} sysmon__kpi`}>
						<img className="sysmon__kpi-icon sysmon__kpi-icon--backup" alt="" />
						<div className="home-summary-card__content">
							<span className="home-summary-card__eyebrow">Zal. DB{data.backup.hasData && data.backup.sizeFmt ? <span className="sysmon__kpi-meta"> ({data.backup.sizeFmt})</span> : null}</span>
							<span className="home-summary-card__value" title={data.backup.sizeFmt ? `Velikost: ${data.backup.sizeFmt}` : ''}>{backupView.label}</span>
						</div>
					</div>
				</>
			) : null}

			{/* 2. řádek (6 karet – měsíc a rekord): Měsíc, Eshop, Za vstupy, Další level, ATH, Tržba / ATH (koláč) */}
			<div className={`home-summary-card ${athToneClass(data.kpi.monthIncome, data.kpi.athRefVal)} sysmon__kpi`}>
				<img className="sysmon__kpi-icon sysmon__kpi-icon--month" alt="" />
				<div className="home-summary-card__content">
					<span className="home-summary-card__eyebrow">Měsíc{data.kpi.monthRankFmt ? <span className="sysmon__kpi-meta"> ({data.kpi.monthRankFmt})</span> : null}</span>
					<span className="home-summary-card__value">{data.kpi.monthIncomeFmt}</span>
				</div>
			</div>

			<div className="home-summary-card sysmon__kpi" title="Nákupy v eshopu za aktuální měsíc">
				<img className="sysmon__kpi-icon sysmon__kpi-icon--eshop" alt="" />
				<div className="home-summary-card__content">
					<span className="home-summary-card__eyebrow">Eshop</span>
					<span className="home-summary-card__value">{data.kpi.eshopMonthFmt}</span>
				</div>
			</div>

			<div className="home-summary-card sysmon__kpi" title="Tržba aktuálního měsíce bez eshopu">
				<img className="sysmon__kpi-icon sysmon__kpi-icon--entry" alt="" />
				<div className="home-summary-card__content">
					<span className="home-summary-card__eyebrow">Za vstupy</span>
					<span className="home-summary-card__value">{data.kpi.entriesMonthFmt}</span>
				</div>
			</div>

			<div className="home-summary-card sysmon__kpi">
				<img className="sysmon__kpi-icon sysmon__kpi-icon--level" alt="" />
				<div className="home-summary-card__content">
					<span className="home-summary-card__eyebrow">Další level</span>
					<span className="home-summary-card__value">{data.kpi.nextLevelRemainingFmt ? `-${data.kpi.nextLevelRemainingFmt}` : data.kpi.nextLevelFmt}</span>
				</div>
			</div>

			<div className="home-summary-card home-summary-card--warning sysmon__kpi">
				<img className="sysmon__kpi-icon sysmon__kpi-icon--ath" alt="" />
				<div className="home-summary-card__content">
					<span className="home-summary-card__eyebrow">ATH{data.kpi.athMonthFmt ? <span className="sysmon__kpi-meta"> ({data.kpi.athMonthFmt})</span> : null}</span>
					<span className="home-summary-card__value">{data.kpi.athFmt}</span>
				</div>
			</div>

			{/* Podíl aktuální tržby na rekordu – koláč sedí místo rohové ikonky (absolutně),
			    takže karta drží přesně stejné rozměry jako ostatní KPI karty v řádku. */}
			<div className="home-summary-card sysmon__kpi sysmon__kpi--athshare" title={athShareTitle}>
				<div className="home-summary-card__content">
					<span className="home-summary-card__eyebrow">Tržba / ATH</span>
					<span className="home-summary-card__value">{data.kpi.athPctFmt}</span>
				</div>
				<div className="sysmon__kpi-donut" aria-hidden="true">
					<MonitorAthDonut value={data.kpi.monthIncome} target={data.kpi.athRefVal} />
				</div>
			</div>
		</div>
	);
}

// Aktuální lekce (dnešní vč. proběhlých + budoucí) s účastníky a stavem platby.
function LessonList({lessons}) {
	return (
		<div className="sysmon__list">
			{(lessons || []).map((lesson) => {
				const isFull = (Number(lesson.USERS_CNT) || 0) >= (Number(lesson.USERS_MAX) || 0);
				const isLocked = Number(lesson.LOCKED) === 1;
				const lockAlt = isLocked ? 'Zamčená lekce' : 'Odemčená lekce';

				return (
					<div className={`sysmon__lesson${Number(lesson.IS_TODAY) === 1 ? ' is-today' : ''}${Number(lesson.IS_PAST) === 1 ? ' is-past' : ''}${Number(lesson.IS_ONGOING) === 1 ? ' is-now' : ''}`} key={lesson.ID}>
						<div className="sysmon__lesson-head">
							<div className="sysmon__lesson-main">
								<span className="sysmon__lesson-date">{lesson.DAY_NAME} {lesson.CRDTS_DT}{lesson.INSTRUCTOR ? <> – <span className="sysmon__lesson-coach">{lesson.INSTRUCTOR}</span></> : ''}</span>
								<div className="sysmon__lesson-timerow">
									<span className="sysmon__lesson-time">
										{lesson.CRDTS_TM_FROM}–{lesson.CRDTS_TM_TO}
									</span>
									{lesson.LESSONNOTE ? <span className="sysmon__lesson-note" title={lesson.LESSONNOTE}>{lesson.LESSONNOTE}</span> : null}
								</div>
							</div>
							<div className="sysmon__lesson-meta">
								<span className={`sysmon__lesson-cap${isFull ? ' is-full' : ' is-available'}`}>
									{lesson.USERS_CNT} / {lesson.USERS_MAX}
								</span>
								<img src={isLocked ? '/images/lesson_lock.svg' : '/images/lesson_unlock.svg'} className="sysmon__lesson-lock" alt={lockAlt} title={lockAlt} />
							</div>
						</div>
						{(Array.isArray(lesson.USERS) ? lesson.USERS : []).length > 0 ? (
							<div className="sysmon__lesson-attendees">
								{lesson.USERS.map((attendee, index) => (
									<span className={`sysmon__lesson-attendee${attendee.LABEL === 'Anonym' ? ' is-anonym' : ''}${Number(attendee.STATUS) === 1 ? ' is-paid' : ''}`} key={`${lesson.ID}_att_${index}`}>
										<span className="sysmon__lesson-attendee-label">{attendee.LABEL}</span>
										{Number(attendee.PERMANENT) === 1 ? <img className="icon2 permanent active sysmon__lesson-attendee-permanent" alt="" title="Permice" /> : null}
										{Number(attendee.ADDPERSON) > 0 ? <span className="sysmon__lesson-attendee-extra">+{attendee.ADDPERSON}</span> : null}
										{Number(attendee.STATUS) === 1 ? <img className="icon1 ok1 sysmon__lesson-attendee-check" alt="" title="Zaplaceno" /> : null}
									</span>
								))}
							</div>
						) : null}
					</div>
				);
			})}
			{(!lessons || lessons.length === 0) ? <div className="sysmon__empty">Žádné lekce</div> : null}
		</div>
	);
}

// Nákupy v eshopu (nepotvrzené = usr_wallet_tmp, dnešní = usr_wallet).
function PurchaseList({purchases, pending = false, emptyText}) {
	return (
		<div className="sysmon__list">
			{(purchases || []).map((purchase) => (
				<div className={`sysmon__purchase${pending ? ' sysmon__purchase--pending' : ''}`} key={pending ? `pending_${purchase.ID}` : purchase.ID}>
					<div className="sysmon__purchase-icon">
						{purchase.ITEMIMG ? <img src={purchase.ITEMIMG} alt="" /> : null}
					</div>
					<div className="sysmon__purchase-main">
						<span className="sysmon__purchase-item">{purchase.ITEMNAME}</span>
						<span className="sysmon__purchase-user">{purchase.USRNAME}</span>
					</div>
					<div className="sysmon__purchase-side">
						<span className="sysmon__purchase-price">{purchase.PRICEFMT}</span>
						<span className="sysmon__purchase-time">{purchase.CRDTS}</span>
					</div>
				</div>
			))}
			{(!purchases || purchases.length === 0) ? <div className="sysmon__empty">{emptyText}</div> : null}
		</div>
	);
}

function IncomeList({incomes}) {
	return (
		<div className="sysmon__list">
			{(incomes || []).map((income) => (
				<div className="sysmon__income" key={income.ID}>
					<div className="sysmon__income-main">
						<span className="sysmon__income-date">{income.PAYMENTDTS}</span>
						<span className="sysmon__income-user">{income.USRNAME || income.NOTE || '—'}</span>
					</div>
					<span className="sysmon__income-amount">{income.AMOUNTFMT}</span>
				</div>
			))}
			{(!incomes || incomes.length === 0) ? <div className="sysmon__empty">Dnes žádné příjmy</div> : null}
		</div>
	);
}

// Ovály dluh (červený) / předplaceno (zelený) + nezaplacené vstupy (oranžový) –
// společné pro dlužníky, končící permice i seznam posledních aktivních uživatelů.
function MoneyBadges({row}) {
	const topay = Number(row.TOPAY_TOTAL) || 0;
	const notPayedTotal = Number(row.NOT_PAYED_TOTAL) || 0;

	return (
		<>
			{topay !== 0 ? (
				<span className={`sysmon__user-badge${topay > 0 ? ' sysmon__user-badge--danger' : ' sysmon__user-badge--success'}`} title={topay > 0 ? 'Dluh' : 'Předplaceno'}>
					{row.TOPAY_TOTAL_FMT}
				</span>
			) : null}
			{notPayedTotal > 0 ? (
				<span className="sysmon__user-badge sysmon__user-badge--warning" title={`Nezaplacené vstupy (${row.NOT_PAYED_CNT}x)`}>
					{row.NOT_PAYED_TOTAL_FMT}
				</span>
			) : null}
		</>
	);
}

// Klik na jméno (nebo telefon) v monitoringu → popup se sekcí Uživatelé s rovnou
// vyhledaným uživatelem – stejný obsah jako /user, jen se aplikace nepřepne.
const userSearchTarget = (row) => ({SEARCH: `${row.LASTNAME} ${row.FIRSTNAME}`, NAME: row.NAME});

// Dlužníci bez permice. Bez pravé části „dnes/zítra", protože tihle uživatelé
// žádnou platnou permici nemají – jen datum, do kdy platila.
function DebtorList({debtors, onOpenUsers}) {
	return (
		<div className="sysmon__list">
			{(debtors || []).map((debtor) => (
				<div className="sysmon__permit" key={debtor.ID}>
					<UserAvatar photoUrl={debtor.PROFILE_PHOTO_URL || ''} displayName={debtor.NAME} className="sysmon__permit-avatar" />
					<div className="sysmon__permit-main">
						{/* Jméno otevře popup Uživatelé s rovnou vyhledaným uživatelem (stejně jako v pravém okně). */}
						<button type="button" className="sysmon__permit-name" onClick={() => onOpenUsers(userSearchTarget(debtor))} title="Zobrazit v sekci Uživatelé">{debtor.NAME}</button>
						<MoneyBadges row={debtor} />
					</div>
					{debtor.PAYEDTO_DT_FMT ? (
						<span className="sysmon__permit-side">
							<span className="sysmon__permit-date" title="Permice byla platná do">{debtor.PAYEDTO_DT_FMT}</span>
						</span>
					) : null}
				</div>
			))}
			{(!debtors || debtors.length === 0) ? <div className="sysmon__empty">Žádní dlužníci bez permice</div> : null}
		</div>
	);
}

function ExpiringPermitList({permits, onOpenUsers}) {
	return (
		<div className="sysmon__list">
			{(permits || []).map((permit) => {
				const daysLeft = Number(permit.DAYS_LEFT);
				const isUrgent = Number.isFinite(daysLeft) && daysLeft <= 1;

				return (
					<div className="sysmon__permit" key={permit.ID}>
						<UserAvatar photoUrl={permit.PROFILE_PHOTO_URL || ''} displayName={permit.NAME} className="sysmon__permit-avatar" />
						<div className="sysmon__permit-main">
							<button type="button" className="sysmon__permit-name" onClick={() => onOpenUsers(userSearchTarget(permit))} title="Zobrazit v sekci Uživatelé">{permit.NAME}</button>
							<MoneyBadges row={permit} />
						</div>
						<span className="sysmon__permit-side">
							<span className="sysmon__permit-date">{permit.PAYEDTO_DT_FMT}</span>
							<span className={`sysmon__permit-left${isUrgent ? ' is-urgent' : ''}`}>{formatDaysLeft(daysLeft)}</span>
						</span>
					</div>
				);
			})}
			{(!permits || permits.length === 0) ? <div className="sysmon__empty">Žádné končící permice</div> : null}
		</div>
	);
}

function EntryBanList({bans, onOpenUsers}) {
	return (
		<div className="sysmon__list">
			{(bans || []).map((ban) => (
				<div className="sysmon__permit" key={ban.ID}>
					<UserAvatar photoUrl={ban.PROFILE_PHOTO_URL || ''} displayName={ban.NAME} className="sysmon__permit-avatar" />
					<div className="sysmon__permit-main sysmon__permit-main--column">
						{/* Jméno otevře popup Uživatelé s vyhledaným uživatelem. */}
						<button type="button" className="sysmon__permit-name" onClick={() => onOpenUsers(userSearchTarget(ban))} title="Zobrazit v sekci Uživatelé">{ban.NAME}</button>
						{/* Poslední aktivita (zeleně = dnešní) + kolik času od ní uplynulo – stejně
						    jako u „posledních aktivních uživatelů", jen navíc s dny. */}
						{ban.LASTACTIVITYDTS ? (
							<span className="sysmon__permit-activity">
								<span className={`sysmon__user-time${parseInt(ban.LASTACTIVITY_TODAY, 10) === 1 ? ' is-today' : ''}`}>
									{ban.LASTACTIVITYDTS}
								</span>
								{formatActivityAgo(ban.LASTACTIVITY_AGO_SEC, true) ? (
									<span className="sysmon__user-activity-ago sysmon__user-activity-ago--light" title="Čas od poslední aktivity">
										{formatActivityAgo(ban.LASTACTIVITY_AGO_SEC, true)}
									</span>
								) : null}
							</span>
						) : null}
					</div>
					{/* Telefon vpravo – popup Uživatelé, vyhledá hlavní číslo bez předvolby a bez mezer. */}
					{ban.PHONES ? (
						<span className="sysmon__permit-side">
							<button type="button" className="sysmon__permit-phone" onClick={() => onOpenUsers({SEARCH: ban.PHONE_SEARCH, NAME: ban.NAME})} title="Zobrazit v sekci Uživatelé">{ban.PHONES}</button>
						</span>
					) : null}
				</div>
			))}
			{(!bans || bans.length === 0) ? <div className="sysmon__empty">Žádný zákaz vstupu</div> : null}
		</div>
	);
}

// Poslední aktivní uživatelé (nebo výsledek vyhledávání).
// compact = mobilní rozložení: pod jménem a metou jeden řádek přes celou šířku
// karty (i pod avatarem) s datem permice vlevo a chipy + ikonkami + ID vpravo. Když se vše nevejde, zalomí se po skupinách (chipy / ikonky), ne po kusech.
function UserList({users, onOpenSessions, onOpenReservations, onOpenLogs, onOpenPayments, onOpenEshop, onOpenBonus, onOpenUsers, compact = false}) {
	return (
		<div className="sysmon__list">
			{(users || []).map((user) => {
				const displayName = `${user.FIRSTNAME || ''} ${user.LASTNAME || ''}`.trim() || user.NAME || '';
				const isPermanent = parseInt(user.PERNAMENTMEMBER, 10) === 1;
				// Kolik dní od poslední rezervace ve fitku (za jménem) + čas té návštěvy v závorce.
				// Budoucí rezervace má přednost – „zítra (20:00)" apod. zeleně.
				const nextVisit = formatNextVisit(user.NEXT_VISIT_DAYS_AHEAD);
				const lastVisit = nextVisit || formatLastVisit(user.LAST_VISIT_DAYS_AGO);
				const lastVisitTm = nextVisit ? user.NEXT_VISIT_TM : user.LAST_VISIT_TM;
				// Datum platnosti permice: žlutě poslední 3 dny, jinak zeleně když platí,
				// bíle když permice není (nebo už vypršela).
				const payedToTone = parseInt(user.PAYEDTO_EXPIRING, 10) === 1
					? ' is-expiring'
					: (parseInt(user.PAYEDTO_VALID, 10) === 1 ? ' is-valid' : '');
				// Do kdy permice platí („dnes" / „zítra" / „3 dny") – jen když nějakou platnou má.
				// Za datum se píše oddělené pomlčkou, barvu dědí po datu; bez permice nic.
				const payedToLeft = parseInt(user.PAYEDTO_VALID, 10) === 1 && user.PAYEDTO_DAYS_LEFT !== null && user.PAYEDTO_DAYS_LEFT !== undefined
					? formatDaysLeftPlain(user.PAYEDTO_DAYS_LEFT)
					: '';
				const hasMessage = parseInt(user.HAS_MESSAGE, 10) === 1;
				const hasChips = hasMessage
					|| (Number(user.TOPAY_TOTAL) || 0) !== 0
					|| (Number(user.NOT_PAYED_TOTAL) || 0) > 0
					|| parseInt(user.SYSADMIN, 10) === 1
					|| parseInt(user.MANAGER, 10) === 1
					|| parseInt(user.INSTRUCTOR, 10) === 1;

				// Datum, dokdy má zaplacenou permici (třetí řádek / vlevo ve spodním řádku).
				const payedTo = user.PAYEDTO_DT_FMT ? (
					<div className={`sysmon__user-payedto${payedToTone}`} title="Permice platná do">
						{user.PAYEDTO_DT_FMT}
						{payedToLeft ? <span className="sysmon__user-payedto-left">– {payedToLeft}</span> : null}
					</div>
				) : null;

				// Oranžový chip s ID – vždy nejvíc vpravo.
				const idChip = <span className="sysmon__user-id">{user.ID}</span>;

				// Zpráva + dluh/předplaceno + nezaplacené vstupy + role (ADM/MAN/INS).
				const chips = (
					<>
						{/* Uživatel má vyplněné pole Zpráva – šedivá tečka jako v /user. */}
						{hasMessage ? (
							<div className="icon_message small" title="Zpráva" />
						) : null}
						<MoneyBadges row={user} />
						{/* Role uživatele (jako v /user): ADM/MAN červeně, INS zeleně – nalevo od ID. */}
						{parseInt(user.SYSADMIN, 10) === 1 ? (
							<span className="sysmon__user-flag sysmon__user-flag--danger" title="Sysadmin">ADM</span>
						) : null}
						{parseInt(user.MANAGER, 10) === 1 ? (
							<span className="sysmon__user-flag sysmon__user-flag--danger" title="Manažer">MAN</span>
						) : null}
						{parseInt(user.INSTRUCTOR, 10) === 1 ? (
							<span className="sysmon__user-flag sysmon__user-flag--success" title="Instruktor">INS</span>
						) : null}
					</>
				);

				// Stavové ikonky (pozvánky, eshop, člen, permice, logy). Sekce se otevírají
				// v popupu pro daného uživatele – obsah je stejný jako ve stránce z hlavního menu,
				// jen se aplikace nepřepne (hlavně na mobilu).
				const icons = (
					<>
						{/* Uživatele někdo pozval (má parenta) – klik otevře sekci Uživatelé s vyhledaným parentem (mentorem). */}
						{parseInt(user.HAS_PARENT, 10) === 1 ? (
							<button type="button" className="sysmon__icon-button" onClick={() => onOpenUsers({SEARCH: user.PARENT_SEARCH, NAME: user.PARENT_NAME})} title={`Pozval: ${user.PARENT_NAME}`} aria-label={`Zobrazit uživatele ${user.PARENT_NAME}`}>
								<img className="icon2 bonus" alt="" />
							</button>
						) : null}
						{/* Pozvánky a bonusy vybraného uživatele – stejný obsah jako /mybonus, jen pro cizí ID. */}
						<button type="button" className="sysmon__icon-button" onClick={() => onOpenBonus(user)} title="Zobrazit pozvánky a bonusy" aria-label={`Zobrazit pozvánky a bonusy uživatele ${user.NAME}`}>
							<img className="icon2 bonus_usr" alt="" />
						</button>
						{/* Eshop sysadmin s rovnou vyhledaným uživatelem. */}
						<button type="button" className="sysmon__icon-button" onClick={() => onOpenEshop(user)} title="Zobrazit eshop" aria-label={`Zobrazit eshop uživatele ${user.NAME}`}>
							<img className="icon2 eshop active" alt="" />
						</button>
						<img className={`icon2 member ${parseInt(user.CLUBMEMBER, 10) === 1 ? 'active' : ''}`} alt="" title="Člen" />
						{/* Ikonka permanentky = Platby s rovnou vyhledaným uživatelem. */}
						<button type="button" className="sysmon__icon-button" onClick={() => onOpenPayments(user)} title="Zobrazit platby" aria-label={`Zobrazit platby uživatele ${user.NAME}`}>
							<img className={`icon2 permanent ${isPermanent ? 'active' : ''}`} alt="" />
						</button>
						<button type="button" className="sysmon__icon-button" onClick={() => onOpenLogs(user)} title="Zobrazit logy" aria-label={`Zobrazit logy uživatele ${user.NAME}`}>
							<img className="icon2 userlog" alt="" />
						</button>
					</>
				);

				return (
					<div className={`sysmon__user${compact ? ' sysmon__user--compact' : ''}${parseInt(user.ACTIVE, 10) === 0 ? ' is-inactive' : ''}`} key={user.ID}>
						{/* Avatar jen na prohlížení – klik otevře náhled fotky (stejná komponenta jako v /user). */}
						<EditableProfilePhoto photoUrl={user.PROFILE_PHOTO_URL} displayName={displayName} canEditPhoto={false} entityKey={user.ID} avatarWidth="4.2rem" avatarHeight="4.2rem" />
						<div className="sysmon__user-main">
							<span className="sysmon__user-name-row">
								{/* Jméno otevře popup Uživatelé s rovnou vyhledaným uživatelem. */}
								<button type="button" className="sysmon__user-name" onClick={() => onOpenUsers(userSearchTarget(user))} title="Zobrazit v sekci Uživatelé">{user.NAME}</button>
								{lastVisit ? <span className={`sysmon__user-lastvisit sysmon__user-lastvisit--${lastVisit.tone}`}>{lastVisit.label}{lastVisitTm ? ` (${lastVisitTm})` : ''}</span> : null}
							</span>
							<div className="sysmon__user-meta">
								<span className={`sysmon__user-time${parseInt(user.LASTACTIVITY_TODAY, 10) === 1 ? ' is-today' : ''}`}>
									{user.LASTACTIVITYDTS || '—'}
								</span>
								<button type="button" className="sysmon__reservations-button" onClick={() => onOpenReservations(user)} title="Zobrazit vstupy" aria-label={`Zobrazit vstupy uživatele ${user.NAME}`}>
									<span>{user.USR_RECORDS}x</span>
								</button>
								<button type="button" className="sysmon__session-button" onClick={() => onOpenSessions(user)} title="Zobrazit sessions" aria-label={`Zobrazit sessions uživatele ${user.NAME}`}>
									<img className="icon2 key active" alt="" />
									<span>{user.SESSION_CNT}x</span>
								</button>
								{formatActivityAgo(user.LASTACTIVITY_AGO_SEC) ? (
									<span className="sysmon__user-activity-ago" title="Čas od poslední aktivity">
										{formatActivityAgo(user.LASTACTIVITY_AGO_SEC)}
									</span>
								) : null}
							</div>
							{!compact ? payedTo : null}
						</div>
						{compact ? (
							// Mobil: spodní řádek přes celou šířku karty – permice vlevo, chipy a ikonky vpravo.
							<div className="sysmon__user-bottom">
								{payedTo}
								{hasChips ? <div className="sysmon__user-side-row sysmon__user-chips">{chips}</div> : null}
								{/* ID vždy nejvíc vpravo – drží se u ikonek, ať zalomí spolu s nimi. */}
								<div className="sysmon__user-side-row sysmon__user-icons">{icons}{idChip}</div>
							</div>
						) : (
							<div className="sysmon__user-side">
								{/* 1. řádek: dluh/předplaceno, nezaplacené vstupy, ID (ID vždy nejvíc vpravo). */}
								<div className="sysmon__user-side-row">
									{chips}
									{idChip}
								</div>
								{/* 2. řádek: stavové ikonky. */}
								<div className="sysmon__user-side-row">{icons}</div>
							</div>
						)}
					</div>
				);
			})}
			{(!users || users.length === 0) ? <div className="sysmon__empty">Žádní uživatelé</div> : null}
		</div>
	);
}

function LogList({logs}) {
	return (
		<div className="sysmon__list">
			{(logs || []).map((log, index) => (
				<div className="sysmon__log" key={index}>
					<div className="sysmon__log-left">
						<span className={`sysmon__log-date${parseInt(log.IS_TODAY, 10) === 1 ? ' is-today' : ''}`}>{log.CRDTS}</span>
						<div className={`sysmon__log-action${parseInt(log.SMARTLOCK, 10) === 1 ? ' is-smartlock' : ''}`}>{log.ACTION}</div>
					</div>
					<div className="sysmon__log-user-wrap">
						<span className="sysmon__log-user" title={log.USR}>{log.USR}</span>
						{log.USRNAME ? <span className="sysmon__log-name" title={log.USRNAME}>{log.USRNAME}</span> : null}
					</div>
				</div>
			))}
			{(!logs || logs.length === 0) ? <div className="sysmon__empty">Žádné logy</div> : null}
		</div>
	);
}

// Levé okno: KPI + měsíční graf + dnešní nákupy + aktuální lekce.
function MonitorLeftPanel({active, onOpenUsers}) {
	const {expanded, toggleExpanded, data, loading, errored, lastUpdated, countdown, handleManualRefresh} =
		useMonitorPanel({act: 'getSysadminMonitorLeft', storageKey: LEFT_STORAGE_KEY, initialData: emptyLeftData, active});

	if (!expanded) {
		return <MonitorFab className="sysmon-fab--left" onClick={toggleExpanded} />;
	}

	const debtorsTotal = sumDebtors(data.debtorsNoPermit);
	const todayIncomeTotal = sumIncomes(data.todayIncomes);

	return (
		<section className="sysmon sysmon--left" aria-label="Sysadmin monitoring">
			<MonitorHeader
				errored={errored}
				loading={loading}
				lastUpdated={lastUpdated}
				countdown={countdown}
				onRefresh={handleManualRefresh}
				onCollapse={toggleExpanded}
			/>

			{/* Nescrollovatelná část: KPI (hned pod hlavičkou) + graf + (nákupy | lekce) */}
			<div className="sysmon__top">
				<KpiCards data={data} />

				<div className="sysmon__section">
					<div className="sysmon__chart">
						<ChartColumn data1={data.incomeMonthly.values} labels={data.incomeMonthly.labels} colorPlus={data.incomeMonthly.colors} stepSize={5000} linesColor={'#aaaaaa'} selectedColumn={() => {}} />
					</div>
				</div>
			</div>

			{/* Spodní část pod grafem se scrolluje jako celek, když je delší (bez pevné výšky seznamů). */}
			<div className="sysmon__scroll">
				<div className="sysmon__cols sysmon__cols--top">
					<div className="sysmon__col">
						<div className="sysmon__section-head">Aktuální lekce</div>
						<LessonList lessons={data.lessons} />

						<div className="sysmon__section-head sysmon__section-head--spaced sysmon__section-head--danger">Nepotvrzené nákupy</div>
						<PurchaseList purchases={data.pendingPurchases} pending emptyText="Žádné nepotvrzené nákupy" />

						<div className="sysmon__section-head sysmon__section-head--spaced">Dnešní nákupy</div>
						<PurchaseList purchases={data.todayPurchases} emptyText="Dnes žádné nákupy" />

						<div className="sysmon__section-head sysmon__section-head--spaced sysmon__section-head--split">
							Dnešní příjmy
							<span className="sysmon__section-total sysmon__section-total--income">{formatMoney2(todayIncomeTotal)}</span>
						</div>
						<IncomeList incomes={data.todayIncomes} />
					</div>

					<div className="sysmon__col">
						<div className="sysmon__section-head sysmon__section-head--split">
							Dlužníci bez permice
							<span className="sysmon__section-total sysmon__section-total--debt">{formatMoney2(debtorsTotal)}</span>
						</div>
						<DebtorList debtors={data.debtorsNoPermit} onOpenUsers={onOpenUsers} />

						<div className="sysmon__section-head sysmon__section-head--spaced">Končící permice</div>
						<ExpiringPermitList permits={data.expiringPermits} onOpenUsers={onOpenUsers} />

						<div className="sysmon__section-head sysmon__section-head--spaced sysmon__section-head--danger">Zákaz vstupu</div>
						<EntryBanList bans={data.entryBans} onOpenUsers={onOpenUsers} />
					</div>
				</div>
			</div>
		</section>
	);
}

// Pravé okno: poslední logy (15) + poslední aktivní uživatelé (11).
// userPopups = otevírání popupů uživatele (sdílené s levým oknem, vykresluje je MonitorDesktop).
function MonitorRightPanel({active, userPopups}) {
	// Vyhledávání v seznamu uživatelů – prázdné pole = posledních 11 aktivních,
	// jinak se hledá stejně jako v sekci /user (parametr jde i s každým refreshem).
	const [searchValue, setSearchValue] = useState('');

	const {expanded, toggleExpanded, data, loading, errored, lastUpdated, countdown, handleManualRefresh} =
		useMonitorPanel({act: 'getSysadminMonitorRight', storageKey: RIGHT_STORAGE_KEY, initialData: emptyRightData, active, params: {search: searchValue}});

	const {openSessionsPopup, openReservationsPopup, openLogsPopup, openPaymentsPopup, openEshopPopup, openBonusPopup, openUsersPopup} = userPopups;

	if (!expanded) {
		return <MonitorFab className="sysmon-fab--right" onClick={toggleExpanded} />;
	}

	return (
		<section className="sysmon sysmon--right" aria-label="Sysadmin monitoring – logy a uživatelé">
			<MonitorHeader
				errored={errored}
				loading={loading}
				lastUpdated={lastUpdated}
				countdown={countdown}
				onRefresh={handleManualRefresh}
				onCollapse={toggleExpanded}
			/>

			{/* Scrollovatelná část: dva sloupce (uživatelé | logy) */}
			<div className="sysmon__scroll">
				<div className="sysmon__cols sysmon__cols--lists">
					<div className="sysmon__col">
						<div className="sysmon__section-head">Poslední aktivní uživatelé</div>
						{/* Stejná komponenta vyhledávání jako v /user – debounce + křížek na smazání. */}
						<div className="sysmon__search">
							<Search showSearch searchValue={searchValue} onChangeSearch={(value) => setSearchValue(value)} />
						</div>
						<UserList users={data.recentUsers} onOpenSessions={openSessionsPopup} onOpenReservations={openReservationsPopup} onOpenLogs={openLogsPopup} onOpenPayments={openPaymentsPopup} onOpenEshop={openEshopPopup} onOpenBonus={openBonusPopup} onOpenUsers={openUsersPopup} />
					</div>

					<div className="sysmon__col">
						<div className="sysmon__section-head">Poslední logy</div>
						<LogList logs={data.userLogs} />
					</div>
				</div>
			</div>
		</section>
	);
}

// Desktop (≥ 1200px): levé + pravé okno. Popupy uživatele mají společné – klik na jméno
// v levém okně (dlužníci, permice, zákaz vstupu) otevře stejný popup Uživatelé jako
// jméno nebo ikonka v pravém okně. Popupy se vykreslují i při sbaleném okně.
function MonitorDesktop({active, usrInfo}) {
	const userPopups = useUserPopups(usrInfo);

	return (
		<>
			<MonitorLeftPanel active={active} onOpenUsers={userPopups.openUsersPopup} />
			<MonitorRightPanel active={active} userPopups={userPopups} />
			{userPopups.popups}
		</>
	);
}

// ---------------------------------------------------------------------------
// Mobil (< 1200px)
// ---------------------------------------------------------------------------

// Tón stavové tečky u sbalené ikonky – jen „je / není problém", ne stav dveří
// (zamčeno je normální stav, červeně by pořád strašilo).
const getTechStatus = (data, loaded) => {
	if (!loaded) {
		return {nuki: '', fio: '', backup: '', issues: 0};
	}

	const nuki = data.nuki.offline || !data.nuki.hasData ? 'warning' : (data.nuki.batteryLow ? 'warning' : 'ok');
	const fio = !data.fio.hasData || data.fio.missingColumn ? 'warning' : (!data.fio.success ? 'danger' : (data.fio.stale ? 'warning' : 'ok'));
	const backup = !data.backup.hasData || data.backup.missingColumn ? 'danger' : (data.backup.tone === 'success' ? 'ok' : data.backup.tone);
	const issues = [nuki, fio, backup].filter((tone) => tone !== 'ok').length;

	return {nuki, fio, backup, issues};
};

// Stavová karta (Dveře / FIO / Záloha) v horním řádku.
function StatusChip({tone, label, value, meta}) {
	return (
		<div className={`home-summary-card ${tone} sysmon-m__chip`}>
			<span className="home-summary-card__eyebrow">{label}</span>
			<span className="home-summary-card__value">{value}</span>
			{meta ? <span className="sysmon-m__chip-meta">{meta}</span> : null}
		</div>
	);
}

const TAB_ICONS = {
	overview: <path d="M4 4h7v7H4zM13 4h7v4h-7zM13 11h7v9h-7zM4 14h7v6H4z" />,
	lessons: <path d="M4 5h16v15H4zM4 10h16M8 3v4M16 3v4" />,
	people: <path d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21a8 8 0 0 1 16 0" />,
	logs: <path d="M5 6h14M5 12h14M5 18h9" />,
};

function MonitorMobile({usrInfo}) {
	const [expanded, toggleExpanded, collapse] = useExpanded(MOBILE_STORAGE_KEY);
	const [tab, setTab] = useState(getInitialMobileTab);
	const [searchValue, setSearchValue] = useState('');

	const selectTab = useCallback((key) => {
		setTab(key);

		try {
			window.sessionStorage.setItem(MOBILE_TAB_STORAGE_KEY, key);
		} catch (error) {
			// sessionStorage nemusí být dostupné – stav zůstane jen v paměti.
		}
	}, []);

	// Levá data běží pořád: rozbalené po 10 s, sbalené po minutě (odznak + tečky).
	const left = useMonitorData({
		act: 'getSysadminMonitorLeft',
		initialData: emptyLeftData,
		active: true,
		intervalSec: expanded ? REFRESH_SEC : MOBILE_IDLE_REFRESH_SEC,
	});

	// Pravá data (uživatelé + logy) jen na záložkách, které je ukazují.
	const needsRight = expanded && (tab === 'people' || tab === 'logs');
	const right = useMonitorData({
		act: 'getSysadminMonitorRight',
		initialData: emptyRightData,
		active: needsRight,
		params: {search: searchValue},
	});

	const {openSessionsPopup, openReservationsPopup, openLogsPopup, openPaymentsPopup, openEshopPopup, openBonusPopup, openUsersPopup, popups} = useUserPopups(usrInfo, collapse);

	const data = left.data;
	const loaded = left.lastUpdated !== '';
	const tech = getTechStatus(data, loaded);

	const pendingCnt = (data.pendingPurchases || []).length;
	const debtorsTotal = sumDebtors(data.debtorsNoPermit);
	const todayIncomeTotal = sumIncomes(data.todayIncomes);

	// Odznak na ikonce: jen co je akutní – nepotvrzené nákupy a technické problémy.
	// Končící permice a dlužníci se nepočítají (běžná agenda, odznak by svítil pořád).
	const badgeCount = loaded ? pendingCnt + tech.issues : 0;
	const badgeTone = 'danger';

	if (!expanded) {
		return (
			<div className="sysmon-mfab">
				{loaded ? (
					<div className="sysmon-mfab__status" title={`Dveře: ${tech.nuki === 'ok' ? 'OK' : 'pozor'} · FIO: ${tech.fio === 'ok' ? 'OK' : 'pozor'} · Záloha: ${tech.backup === 'ok' ? 'OK' : 'pozor'}`} aria-hidden="true">
						<i className={`is-${tech.nuki}`} />
						<i className={`is-${tech.fio}`} />
						<i className={`is-${tech.backup}`} />
					</div>
				) : null}
				<MonitorFab className="sysmon-fab--mobile" onClick={toggleExpanded}>
					{badgeCount > 0 ? <span className={`sysmon-mfab__badge sysmon-mfab__badge--${badgeTone}`}>{badgeCount}</span> : null}
				</MonitorFab>
			</div>
		);
	}

	const usesRight = tab === 'people' || tab === 'logs';
	const headState = usesRight ? right : left;
	const activeTab = MOBILE_TABS.find((item) => item.key === tab) || MOBILE_TABS[0];
	const fioView = getFioView(data.fio);
	const backupView = getBackupView(data.backup);

	return (
		<>
		<section className="sysmon sysmon--mobile" aria-label="Sysadmin monitoring">
			<MonitorHeader
				title={activeTab.label}
				errored={headState.errored}
				loading={headState.loading}
				lastUpdated={headState.lastUpdated}
				countdown={headState.countdown}
				onRefresh={headState.handleManualRefresh}
				onCollapse={toggleExpanded}
			/>

			<div className="sysmon__scroll" key={tab}>
				{tab === 'overview' ? (
					<>
						<div className="sysmon-m__chips">
							<StatusChip
								tone={getNukiCardTone(data.nuki)}
								label="Dveře"
								value={data.nuki.offline ? 'offline' : data.nuki.stateLabel}
								meta={[data.nuki.offline ? '' : data.nuki.batteryFmt, data.nuki.checkedAgoFmt ? `před ${data.nuki.checkedAgoFmt}` : ''].filter(Boolean).join(' · ')}
							/>
							<StatusChip
								tone={fioView.tone}
								label="FIO"
								value={fioView.label}
								meta={data.fio.checkedAgoFmt ? `před ${data.fio.checkedAgoFmt}` : ''}
							/>
							<StatusChip
								tone={backupView.tone}
								label="Záloha"
								value={backupView.label}
								meta={data.backup.hasData ? data.backup.sizeFmt : ''}
							/>
						</div>

						<div className="sysmon__section">
							<div className="sysmon__section-head">Dnes a měsíc</div>
							<KpiCards data={data} showTech={false} />
						</div>

						<div className="sysmon__section">
							<div className="sysmon__section-head">Tržby po měsících</div>
							<div className="sysmon__chart">
								<ChartColumn data1={data.incomeMonthly.values} labels={data.incomeMonthly.labels} colorPlus={data.incomeMonthly.colors} stepSize={5000} linesColor={'#aaaaaa'} selectedColumn={() => {}} />
							</div>
						</div>

						<div className="sysmon__section">
							<div className="sysmon__section-head sysmon__section-head--danger">Nepotvrzené nákupy</div>
							<PurchaseList purchases={data.pendingPurchases} pending emptyText="Žádné nepotvrzené nákupy" />
						</div>

						<div className="sysmon__section">
							<div className="sysmon__section-head">Dnešní nákupy</div>
							<PurchaseList purchases={data.todayPurchases} emptyText="Dnes žádné nákupy" />
						</div>

						<div className="sysmon__section">
							<div className="sysmon__section-head sysmon__section-head--split">
								Dnešní příjmy
								<span className="sysmon__section-total sysmon__section-total--income">{formatMoney2(todayIncomeTotal)}</span>
							</div>
							<IncomeList incomes={data.todayIncomes} />
						</div>

						<div className="sysmon__section">
							<div className="sysmon__section-head sysmon__section-head--split">
								Dlužníci bez permice
								<span className="sysmon__section-total sysmon__section-total--debt">{formatMoney2(debtorsTotal)}</span>
							</div>
							<DebtorList debtors={data.debtorsNoPermit} onOpenUsers={openUsersPopup} />
						</div>

						<div className="sysmon__section">
							<div className="sysmon__section-head">Končící permice</div>
							<ExpiringPermitList permits={data.expiringPermits} onOpenUsers={openUsersPopup} />
						</div>

						<div className="sysmon__section">
							<div className="sysmon__section-head sysmon__section-head--danger">Zákaz vstupu</div>
							<EntryBanList bans={data.entryBans} onOpenUsers={openUsersPopup} />
						</div>
					</>
				) : null}

				{tab === 'lessons' ? (
					<div className="sysmon__section">
						<div className="sysmon__section-head">Aktuální lekce</div>
						<LessonList lessons={data.lessons} />
					</div>
				) : null}

				{tab === 'people' ? (
					<div className="sysmon__section">
						<div className="sysmon__section-head">Poslední aktivní uživatelé</div>
						<div className="sysmon__search">
							<Search showSearch searchValue={searchValue} onChangeSearch={(value) => setSearchValue(value)} />
						</div>
						<UserList users={right.data.recentUsers} onOpenSessions={openSessionsPopup} onOpenReservations={openReservationsPopup} onOpenLogs={openLogsPopup} onOpenPayments={openPaymentsPopup} onOpenEshop={openEshopPopup} onOpenBonus={openBonusPopup} onOpenUsers={openUsersPopup} compact />
					</div>
				) : null}

				{tab === 'logs' ? (
					<div className="sysmon__section">
						<div className="sysmon__section-head">Poslední logy</div>
						<LogList logs={right.data.userLogs} />
					</div>
				) : null}
			</div>

			<nav className="sysmon-m__tabs" aria-label="Sekce monitoringu">
				{MOBILE_TABS.map((item) => {
					const badge = item.key === 'overview' ? badgeCount : (item.key === 'lessons' ? (data.lessons || []).length : 0);

					return (
						<button type="button" className={`sysmon-m__tab${tab === item.key ? ' is-active' : ''}`} key={item.key} onClick={() => selectTab(item.key)} aria-current={tab === item.key ? 'page' : undefined}>
							<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
								{TAB_ICONS[item.key]}
							</svg>
							<span>{item.label}</span>
							{badge > 0 ? <span className={`sysmon-m__tab-badge${item.key === 'overview' ? ` sysmon-m__tab-badge--${badgeTone}` : ''}`}>{badge}</span> : null}
						</button>
					);
				})}
			</nav>
		</section>

		{popups}
		</>
	);
}

export default function SysadminMonitor({usrInfo}) {
	const isSysAdmin = Boolean(usrInfo) && usrInfo.sysAdmin === 1;

	const [isLargeScreen, setIsLargeScreen] = useState(getInitialLargeScreen);

	// Sleduj, zda je viewport na velkém rozlišení (≥1200px).
	useEffect(() => {
		if (typeof window === 'undefined' || !window.matchMedia) {
			return undefined;
		}

		const mql = window.matchMedia(LARGE_SCREEN_QUERY);
		const handler = (event) => setIsLargeScreen(event.matches);

		setIsLargeScreen(mql.matches);

		if (mql.addEventListener) {
			mql.addEventListener('change', handler);
		} else {
			mql.addListener(handler);
		}

		return () => {
			if (mql.removeEventListener) {
				mql.removeEventListener('change', handler);
			} else {
				mql.removeListener(handler);
			}
		};
	}, []);

	if (!isSysAdmin) {
		return null;
	}

	// Pod 1200px (mobil, tablet na výšku) jedno okno přes celou obrazovku se záložkami.
	if (!isLargeScreen) {
		return <MonitorMobile usrInfo={usrInfo} />;
	}

	return (
		<>
			<MonitorDesktop active={isSysAdmin && isLargeScreen} usrInfo={usrInfo} />
		</>
	);
}
